import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  inject,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  OnChanges,
  PLATFORM_ID,
  QueryList,
  SimpleChanges,
  Input,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { ScrollToTop } from '../../components/common/scroll-to-top/scroll-to-top';
import { FormsModule } from '@angular/forms';
import { LabelComponent } from '../../components/form/label/lable';
import { InputFieldComponent } from '../../components/form/input/input-field';
import { DatePicker } from '../../components/form/date-picker/date-picker';
import { ButtonComponent } from '../../components/ui/button/button';
import { EventService, IEvent, IEventInfo, ReactionType } from '../../shared/services/event.service';
import { takeUntil, debounceTime, distinctUntilChanged, Subject as RxSubject } from 'rxjs';
import { Subject } from 'rxjs';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
// import { ReactionSoundService } from '../../shared/services/reaction-sound.service';
import { Modal } from '../../components/ui/modal/modal';
import { FriendRequestService, FriendshipStatus } from '../../shared/services/friend-request.service';
import { RelationshipService } from '../../shared/services/relationship.service';
import { ConfirmationPopup } from '../../components/ui/confirmation-popup/confirmation-popup';
import { AuthStateService } from '../../shared/states/auth.state';
import { FavouriteService } from '../../shared/services/favourites.service';
import { ShareSheetComponent } from '../../components/ui/share-sheet/share-sheet';
import { ShareConfig } from '../../shared/services/share.service';
import { FriendRequestLimitPopup } from '../../components/ui/friend-request-limit-popup/friend-request-limit-popup';
import { syncPacContainerVisibility } from '../../shared/utils/google-places.util';
import { SwipeDirective } from '../../shared/directives/swipe.directive';
import { ReportModal } from '../../components/ui/report-modal/report-modal';
import { ReportService } from '../../shared/services/report.service';
import { BlockUserConfirm } from '../../components/ui/block-user-confirm/block-user-confirm';

declare const google: any;

export const REACTIONS: {
  type: ReactionType;
  emoji: string;
  label: string;
  color: string;
  bg: string;
}[] = [
    { type: 'LIKE', emoji: '👍', label: 'Like', color: '#2078f4', bg: '#e7f3ff' },
    { type: 'HEART', emoji: '❤️', label: 'Love', color: '#f33e58', bg: '#ffe7eb' },
    { type: 'HUG', emoji: '🤗', label: 'Hug', color: '#f7b125', bg: '#fff4e0' },
    { type: 'WOW', emoji: '😮', label: 'Wow', color: '#f7b125', bg: '#fff4e0' },
    { type: 'SAD', emoji: '😢', label: 'Sad', color: '#7c93a3', bg: '#eef2f5' },
    { type: 'HAHA', emoji: '😂', label: 'Haha', color: '#f7b125', bg: '#fff4e0' },
  ];

interface ReactionCounts {
  LIKE: number; HEART: number; HUG: number;
  WOW: number; SAD: number; HAHA: number;
}

interface EventCard {
  id: string;
  hostName: string;
  hostAvatar: string;
  hostId: string;
  hostGender: 'f' | 'm';
  timeAgo: string;
  images: string[];
  title: string;
  startDateLabel: string;
  endDateLabel: string;
  startDateISO: string;
  activeSlide: number;
  location: string;
  hashtags: string[];
  commentsDisabled: boolean;
  minPrice: number;
  maxPrice: number;
  likes: number;
  comments: number;
  shares: number;
  createdAt: string;
  myReaction: ReactionType | null;
  reactionCounts: ReactionCounts;
  totalReactions: number;
  recentUserName: string | null;
  topTypes: ReactionType[];
  showEmojiPicker: boolean;
  hoveredReaction: ReactionType | null;
  friendshipStatus: FriendshipStatus;
  friendshipRequestId: string | null;
  linkToBookingSite: string | null;
  isMyPostedEvent: boolean;
  isFollowed: boolean;
  isFavourite: boolean;
  /** Sequence index assigned by the "All" page mixed feed for CSS `order`. */
  globalOrder?: number;
  acceptedCollaborators: Array<{
    _id: string;
    displayName: string;
    userName: string;
    profileImage?: { urlSmall?: string };
  }>;
  mentions: Array<{
    _id: string;
    displayName: string;
    userName: string;
    profileImage?: { urlSmall?: string };
  }>;
  /** True only when this event was removed by an admin after a report —
   *  the backend only ever includes a removed event in the response when
   *  the viewer is its own author. */
  isRemovedByAdmin: boolean;
}

// ── Masonry constants (must match CSS) ────────────────────────────────────────
const ROW_UNIT = 10;  // px — matches grid-auto-rows in CSS
const ROW_GAP = 20;  // px — vertical gap between cards
const USER_PAGE_SIZE = 10;

@Component({
  selector: 'app-events',
  standalone: true,
  imports: [CommonModule, ScrollToTop, FormsModule, LabelComponent, DatePicker, ButtonComponent, Modal, RouterModule, ConfirmationPopup, ShareSheetComponent, FriendRequestLimitPopup, SwipeDirective, ReportModal, BlockUserConfirm],
  templateUrl: './events.html',
  styles: [':host { display: contents; }'],
})
export class Events implements AfterViewInit, OnInit, OnDestroy, OnChanges {
  @Input() userName?: string;
  @Input() embedded = false;
  @Input() featured = false;
  /** Home page only: lay cards out in an equal-height grid instead of
   *  masonry (fixed media frame, clamped text, footer pinned to the bottom).
   *  Off everywhere else, so every other page keeps the masonry layout. */
  @Input() uniform = false;
  @Input() favouritesMode = false;
  /** When true (profile page toggle), only shows events where `userName` is an
   *  accepted collaborator rather than the host. */
  @Input() collabOnly = false;
  @Input() gridCols = 0;

  /** When provided (by the "All" page mixed feed), renders these pre-fetched
   *  items instead of fetching its own. */
  @Input() mixedItems?: any[];
  /** True when embedded in the "All" page mixed feed. */
  @Input() mixedMode = false;

  // ── Price filter ──────────────────────────────────────────────────────────
  minPrice = 0;
  maxPrice = 0;
  priceFrom = 0;
  priceTo = 0;
  draggingThumb: 'from' | 'to' | null = null;

  // ── Location filter ───────────────────────────────────────────────────────
  locationText = '';

  // ── Search filter ─────────────────────────────────────────────────────────
  searchText = '';
  searchInputValue = '';
  showSearchDrop = false;
  trendingSuggestions: string[] = [];
  private searchDrop$ = new Subject<string>();

  // ── Date filter ───────────────────────────────────────────────────────────
  byDate = '';
  byDateDisplay = '';
  dateValue: any;

  // ── Category filter ───────────────────────────────────────────────────────
  selectedCategories: string[] = [];
  categorySearchText = '';
  showMobileFilters = false;

  get filteredCategoryOptions() {
    const q = this.categorySearchText.trim().toLowerCase();
    if (!q) return this.categoryOptions;
    return this.categoryOptions.filter(cat => cat.label.toLowerCase().includes(q));
  }

  clearCategorySearch(): void {
    this.categorySearchText = '';
  }

  // ── Events data ───────────────────────────────────────────────────────────
  events: EventCard[] = [];
  currentPage = 1;
  isLoading = false;
  hasMore = true;
  totalEvents = 0;

  // ── Reaction modal ────────────────────────────────────────────────────────
  readonly REACTIONS = REACTIONS;
  isReactionOpen = false;
  reactionData: any = null;
  reactionsLoading = false;
  activeReactionFilter: ReactionType | null = null;
  private activeReactionEventId: string | null = null;

  // ── Timers ────────────────────────────────────────────────────────────────
  private priceFilterTimer: any = null;
  private filterDebounceTimer: any = null;

  categoryOptions = [
    { value: 'Social / Meet & Greet',        label: 'Social / Meet & Greet' },
    { value: 'Munch',                        label: 'Munch' },
    { value: 'Discussion Group',             label: 'Discussion Group' },
    { value: 'Community Gathering',          label: 'Community Gathering' },
    { value: 'Workshop / Class',             label: 'Workshop / Class' },
    { value: 'Training Course',              label: 'Training Course' },
    { value: 'Talk / Speaker Event',         label: 'Talk / Speaker Event' },
    { value: 'Conference',                   label: 'Conference' },
    { value: 'Retreat',                      label: 'Retreat' },
    { value: 'Festival',                     label: 'Festival' },
    { value: 'Club Night',                   label: 'Club Night' },
    { value: 'Party',                        label: 'Party' },
    { value: 'Play Party',                   label: 'Play Party' },
    { value: 'Speed Dating',                 label: 'Speed Dating' },
    { value: 'Dating Event',                 label: 'Dating Event' },
    { value: 'Singles Event',                label: 'Singles Event' },
    { value: 'Couples Event',                label: 'Couples Event' },
    { value: 'Online Event',                 label: 'Online Event' },
    { value: 'Fundraiser / Charity Event',   label: 'Fundraiser / Charity Event' },
    { value: 'Wellness / Self-Development',  label: 'Wellness / Self-Development' },
    { value: 'Polyamory',                    label: 'Polyamory' },
    { value: 'Open Relationships',           label: 'Open Relationships' },
    { value: 'Ethical Non-Monogamy',         label: 'Ethical Non-Monogamy' },
    { value: 'Swingers',                     label: 'Swingers' },
    { value: 'BDSM',                         label: 'BDSM' },
    { value: 'Kink',                         label: 'Kink' },
    { value: 'Fetish',                       label: 'Fetish' },
    { value: 'Rope / Shibari',               label: 'Rope / Shibari' },
    { value: 'Dominance & Submission (D/s)', label: 'Dominance & Submission (D/s)' },
    { value: 'Master / Slave (M/s)',         label: 'Master / Slave (M/s)' },
    { value: 'Tantra',                       label: 'Tantra' },
    { value: 'Sacred Sexuality',             label: 'Sacred Sexuality' },
    { value: 'LGBTQ+',                       label: 'LGBTQ+' },
    { value: 'Gay Men',                      label: 'Gay Men' },
    { value: 'Lesbian',                      label: 'Lesbian' },
    { value: 'Bisexual',                     label: 'Bisexual' },
    { value: 'Trans & Non-Binary',           label: 'Trans & Non-Binary' },
    { value: 'Women Only',                   label: 'Women Only' },
    { value: 'Men Only',                     label: 'Men Only' },
    { value: 'Sober Event',                  label: 'Sober Event' },
    { value: 'Beginners Welcome',            label: 'Beginners Welcome' },
    { value: 'Accessibility Friendly',       label: 'Accessibility Friendly' },
    { value: 'Other',                        label: 'Other' },
  ];

  @ViewChild('scrollSentinel') scrollSentinel!: ElementRef;
  @ViewChild('masonryGrid') masonryGrid!: ElementRef;
  @ViewChild('sliderTrack') sliderTrack!: ElementRef<HTMLElement>;
  @ViewChild('searchInput') searchInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('locationFilterInput') locationFilterInput?: ElementRef<HTMLInputElement>;
  /** Optional — the filter sidebar (and so the picker) only renders when !embedded. */
  @ViewChild(DatePicker) datePicker?: DatePicker;
  private locationAutocomplete?: any;

  // ── CSS Grid masonry refs ─────────────────────────────────────────────────
  @ViewChildren('eventCardRef') eventCardRefs!: QueryList<ElementRef<HTMLElement>>;

  private destroy$ = new Subject<void>();
  private intersectionObserver?: IntersectionObserver;
  private resizeObserver?: ResizeObserver;
  private cardChangeSub?: any;

  private friendRequestService = inject(FriendRequestService);
  private relationshipService = inject(RelationshipService);
  private authState = inject(AuthStateService);
  private favouriteService = inject(FavouriteService);

  // ── Login prompt state ─────────────────────────────────────────────────────
  showLoginPrompt = false;
  loginPromptUserName = '';
  loginPromptAction = '';
  showFriendLimitPopup = false;
  friendLimitHours: number | null = null;

  // ── Event action menu (3-dot) ──────────────────────────────────────────────
  activeActionEventId: string | null = null;
  showDeleteEventConfirm = false;
  private eventToDelete: any = null;

  // ── Report event modal ───────────────────────────────────────────────────
  isReportModalOpen = false;
  isSubmittingReport = false;
  reportError = '';
  reportSuccessOpen = false;
  private eventToReport: EventCard | null = null;
  private reportService = inject(ReportService);

  // ── Block user (safeword-confirm) modal ─────────────────────────────────
  showBlockUserConfirm = false;
  private eventToBlock: EventCard | null = null;

  /** Bound to <app-block-user-confirm>'s [targetName] — "You're about to
   *  block {name}". */
  get eventToBlockDisplayName(): string {
    return this.eventToBlock?.hostName ?? '';
  }

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private eventService: EventService,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    // private soundService: ReactionSoundService,
    private router: Router,
    private zone: NgZone,
  ) { }

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  ngOnInit() {
    if (this.mixedMode) {
      this.applyMixedItems();
      return;
    }

    this.route.queryParams.subscribe(params => {
      // Pre-apply categories from query param (e.g. deep-link from event details)
      if (params['categories']) {
        const incoming = params['categories'].split(',').map((c: string) => c.trim()).filter(Boolean);
        this.selectedCategories = incoming;
      }
      // Pre-apply a global search / hashtag filter (from the header search box,
      // the "All" page, or a #hashtag click on a post/blog/event) — reuses the
      // existing title+hashtag search this component already supports.
      const incomingSearch = (params['hashtag'] || params['q'] || '').trim();
      if (incomingSearch) {
        this.searchText = params['hashtag'] ? `#${params['hashtag']}` : incomingSearch;
        this.searchInputValue = this.searchText;
      }
      this.resetAndLoad();
    });
    this.eventService.onRefreshEvents$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => { this.resetAndLoad(); });

    this.searchDrop$
      .pipe(debounceTime(250), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(q => this.fetchSuggestions(q));
  }

  ngAfterViewInit() {
    if (!isPlatformBrowser(this.platformId)) return;

    // ── Infinite scroll observer ──────────────────────────────────────────
    this.intersectionObserver = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && !this.isLoading && this.hasMore) {
          this.loadMore();
        }
      },
      { threshold: 0.1 },
    );
    if (this.scrollSentinel?.nativeElement) {
      this.intersectionObserver.observe(this.scrollSentinel.nativeElement);
    }

    // ── Location filter: Google Places autocomplete ────────────────────────
    setTimeout(() => this.initLocationAutocomplete(), 100);

    // ── CSS Grid masonry setup ────────────────────────────────────────────

    // Re-span whenever Angular adds new cards via *ngFor
    this.cardChangeSub = this.eventCardRefs.changes.subscribe(() => {
      this.zone.runOutsideAngular(() => {
        requestAnimationFrame(() => this.spanAllCards());
      });
    });

    // Initial span after first render
    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        this.spanAllCards();
        setTimeout(() => this.setupScrollObserverAfterSpan(), 50);
      });
    });

    // Re-span on window/container resize (sidebar collapses, breakpoints)
    // Skipped in mixedMode: on the "All" page, Posts/Blog/Events are mounted
    // together, and each respanning its cards changes document height — with
    // three document-level observers that becomes a feedback loop (each one's
    // respan re-triggers the other two), pegging the CPU and hanging the tab.
    // cardChangeSub already keeps individual cards sized correctly as they're added.
    if (!this.mixedMode) {
      this.resizeObserver = new ResizeObserver(() => {
        this.zone.runOutsideAngular(() => {
          requestAnimationFrame(() => this.spanAllCards());
        });
      });
      this.resizeObserver.observe(document.body);
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.intersectionObserver?.disconnect();
    this.resizeObserver?.disconnect();
    this.cardChangeSub?.unsubscribe?.();
    if (this.priceFilterTimer) clearTimeout(this.priceFilterTimer);
    if (this.filterDebounceTimer) clearTimeout(this.filterDebounceTimer);
  }

  // ── CSS Grid masonry helpers ───────────────────────────────────────────────

  /** Measure every card and set grid-row: span N. */
  private spanAllCards(): void {
    if (!this.eventCardRefs) return;
    this.eventCardRefs.forEach(ref => this.spanCard(ref.nativeElement));
  }

  /**
   * Measure a single card and set its grid-row span.
   * Reset first so getBoundingClientRect() returns the natural height.
   */
  private spanCard(card: HTMLElement): void {
    card.style.gridRow = '';
    if (this.uniform) return; // equal-height grid: no masonry row spans
    const height = card.getBoundingClientRect().height;
    const span = Math.ceil((height + ROW_GAP) / ROW_UNIT);
    card.style.gridRow = `span ${span}`;
  }

  /**
   * Called from (load) on every <img> inside a card.
   * Re-spans that specific card so its row count reflects the loaded image.
   */
  onEventImageLoad(event: Event): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const img = event.target as HTMLImageElement;
    const card = img.closest<HTMLElement>('.events-masonry-item');
    if (!card) return;
    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => this.spanCard(card));
    });
  }

  // ── Slide change — re-span after new image loads ───────────────────────────

  prevSlide(event: EventCard, e: MouseEvent): void {
    e.stopPropagation();
    event.activeSlide = event.activeSlide > 0
      ? event.activeSlide - 1
      : event.images.length - 1;
    this.respanAfterSlide(e);
  }

  nextSlide(event: EventCard, e: MouseEvent): void {
    e.stopPropagation();
    event.activeSlide = event.activeSlide < event.images.length - 1
      ? event.activeSlide + 1
      : 0;
    this.respanAfterSlide(e);
  }

  goToSlide(event: EventCard, index: number, e: MouseEvent): void {
    event.activeSlide = index;
    this.respanAfterSlide(e);
  }

  /** Touch swipe on the card carousel. `dir` is +1 for next, -1 for previous. */
  onSlideSwipe(event: EventCard, dir: 1 | -1, host: HTMLElement): void {
    const total = event.images.length;
    if (total <= 1) return;
    event.activeSlide = (event.activeSlide + dir + total) % total;
    this.cdr.detectChanges();
    this.respanCard(host.closest<HTMLElement>('.events-masonry-item'));
  }

  /**
   * After a slide change:
   * - Case A (cached image): two rAF ticks let Angular swap block/hidden and
   *   browser repaint, then we span the card immediately.
   * - Case B (uncached image): attach a one-time load listener on the newly
   *   visible <img> so we span once the browser has its natural height.
   */
  private respanAfterSlide(e: Event): void {
    this.respanCard((e.target as HTMLElement).closest<HTMLElement>('.events-masonry-item'));
  }

  private respanCard(card: HTMLElement | null): void {
    if (!card) return;

    // Case A — cached
    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.spanCard(card));
      });
    });

    // Case B — uncached
    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        const activeImg = card.querySelector<HTMLImageElement>('img.block, img:not(.hidden)');
        if (activeImg && !activeImg.complete) {
          const handler = () => {
            requestAnimationFrame(() => this.spanCard(card));
            activeImg.removeEventListener('load', handler);
          };
          activeImg.addEventListener('load', handler);
        }
      });
    });
  }

  // ── Scroll observer (re-attached after initial span) ──────────────────────

  private setupScrollObserverAfterSpan(): void {
    // IntersectionObserver was already set up in ngAfterViewInit — nothing extra needed.
    // This hook exists in case you want to recalibrate after first span.
  }

  // ── Custom dual-thumb slider ───────────────────────────────────────────────

  onTrackMouseDown(event: MouseEvent): void {
    event.preventDefault();
    this.draggingThumb = this.resolveThumb(event.clientX);
    this.cdr.detectChanges();
  }

  onTrackTouchStart(event: TouchEvent): void {
    event.preventDefault();
    this.draggingThumb = this.resolveThumb(event.touches[0].clientX);
    this.cdr.detectChanges();
  }

  @HostListener('document:mousemove', ['$event'])
  onDocumentMouseMove(event: MouseEvent): void {
    if (!this.draggingThumb) return;
    event.preventDefault();
    this.applyDrag(event.clientX);
  }

  @HostListener('document:touchmove', ['$event'])
  onDocumentTouchMove(event: TouchEvent): void {
    if (!this.draggingThumb) return;
    event.preventDefault();
    this.applyDrag(event.touches[0].clientX);
  }

  @HostListener('document:mouseup')
  @HostListener('document:touchend')
  onDocumentPointerUp(): void {
    if (!this.draggingThumb) return;
    this.draggingThumb = null;
    this.onPriceFilterChange();
    this.cdr.detectChanges();
  }

  private resolveThumb(clientX: number): 'from' | 'to' {
    if (!this.sliderTrack?.nativeElement) return 'from';
    const rect = this.sliderTrack.nativeElement.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const value = this.minPrice + ratio * (this.maxPrice - this.minPrice);
    return Math.abs(value - this.priceFrom) <= Math.abs(value - this.priceTo) ? 'from' : 'to';
  }

  private applyDrag(clientX: number): void {
    if (!this.sliderTrack?.nativeElement || !this.draggingThumb) return;
    const rect = this.sliderTrack.nativeElement.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const value = Math.round(this.minPrice + ratio * (this.maxPrice - this.minPrice));
    if (this.draggingThumb === 'from') {
      this.priceFrom = Math.min(value, this.priceTo);
    } else {
      this.priceTo = Math.max(value, this.priceFrom);
    }
    this.cdr.detectChanges();
  }

  // ── Search dropdown ────────────────────────────────────────────────────────

  onSearchInputChange(value: string): void {
    this.searchInputValue = value;
    this.showSearchDrop = true;
    this.searchDrop$.next(value);
  }

  onSearchFocus(): void {
    this.showSearchDrop = true;
    this.fetchSuggestions(this.searchInputValue);
  }

  private fetchSuggestions(q: string): void {
    const cleanQ = q.replace(/^#+/, '').trim();
    this.eventService.getTrendingSearches(cleanQ).subscribe({
      next: (res: any) => {
        this.trendingSuggestions = res?.data ?? res ?? [];
        this.cdr.detectChanges();
      },
      error: () => { this.trendingSuggestions = []; },
    });
  }

  selectSuggestion(suggestion: string): void {
    this.searchInputValue = suggestion;
    this.searchText = suggestion;
    this.showSearchDrop = false;
    this.triggerFilterReload();
  }

  onSearchKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.searchText = this.searchInputValue;
      this.showSearchDrop = false;
      this.triggerFilterReload();
    }
    if (event.key === 'Escape') { this.showSearchDrop = false; }
  }

  clearSearch(): void {
    this.searchText = '';
    this.searchInputValue = '';
    this.showSearchDrop = false;
    this.triggerFilterReload();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.search-dropdown-wrapper')) {
      this.showSearchDrop = false;
      this.cdr.detectChanges();
    }
    if (this.activeActionEventId !== null) {
      this.activeActionEventId = null;
      this.cdr.detectChanges();
    }
  }

  // ── Location ───────────────────────────────────────────────────────────────

  onLocationKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') { this.triggerFilterReload(); }
  }

  private initLocationAutocomplete(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this.locationFilterInput?.nativeElement) return;
    try {
      const inputEl = this.locationFilterInput.nativeElement;
      if (!this.locationAutocomplete) {
        this.locationAutocomplete = new google.maps.places.Autocomplete(inputEl, { types: ['geocode'] });
        syncPacContainerVisibility();
      }
      google.maps.event.clearListeners(this.locationAutocomplete, 'place_changed');
      this.locationAutocomplete.addListener('place_changed', () => {
        const place = this.locationAutocomplete.getPlace();
        if (place.formatted_address) {
          this.locationText = place.formatted_address;
          this.hidePlacesAutocompletePanel();
          this.triggerFilterReload();
          this.cdr.detectChanges();
        }
      });
    } catch (e) { console.warn('Google Autocomplete init failed:', e); }
  }

  private hidePlacesAutocompletePanel(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    window.setTimeout(() => {
      document.querySelectorAll('.pac-container').forEach(p => ((p as HTMLElement).style.display = 'none'));
    }, 0);
  }

  clearLocation(): void {
    this.locationText = '';
    this.triggerFilterReload();
  }

  // ── Date filter ────────────────────────────────────────────────────────────

  handleDateChange(event: any): void {
    this.dateValue = event;
    if (!event) {
      this.byDate = ''; this.byDateDisplay = '';
    } else if (event?.dateStr) {
      this.byDate = event.dateStr; this.byDateDisplay = event.dateStr;
    } else if (event instanceof Date) {
      this.byDate = event.toISOString().split('T')[0];
      this.byDateDisplay = event.toLocaleDateString();
    } else if (typeof event === 'string' && event.trim()) {
      this.byDate = event.trim(); this.byDateDisplay = event.trim();
    } else {
      this.byDate = ''; this.byDateDisplay = '';
    }
    this.triggerFilterReload();
  }

  /** Chip "×" — clears the picker's input too, which `byDate` alone doesn't do. */
  clearDateFilter(): void {
    this.dateValue = null;
    this.byDate = '';
    this.byDateDisplay = '';
    this.datePicker?.clear();
    this.triggerFilterReload();
  }

  // ── Category filter ────────────────────────────────────────────────────────

  toggleCategory(value: string): void {
    if (this.selectedCategories.includes(value)) {
      this.selectedCategories = this.selectedCategories.filter(c => c !== value);
    } else {
      this.selectedCategories = [...this.selectedCategories, value];
    }
    this.triggerFilterReload();
  }

  toggleMobileFilters(): void {
    this.showMobileFilters = !this.showMobileFilters;
  }

  // ── Price filter ───────────────────────────────────────────────────────────

  onPriceFilterChange(): void {
    if (this.priceFilterTimer) clearTimeout(this.priceFilterTimer);
    this.priceFilterTimer = setTimeout(() => { this.triggerFilterReload(); }, 500);
  }

  clearPriceFilter(): void {
    this.priceFrom = this.minPrice;
    this.priceTo = this.maxPrice;
    this.triggerFilterReload();
  }

  // ── Unified filter reload ──────────────────────────────────────────────────

  private triggerFilterReload(): void {
    // Collapse the mobile filter panel once something is applied — it fills the
    // screen on a phone, so leaving it open makes it look like nothing happened.
    // Every filter funnels through here, and it's a no-op on lg+ where the
    // sidebar is always visible (`lg:block` wins over the toggled class).
    this.showMobileFilters = false;
    if (this.filterDebounceTimer) clearTimeout(this.filterDebounceTimer);
    this.filterDebounceTimer = setTimeout(() => {
      this.currentPage = 1;
      this.events = [];
      this.hasMore = true;
      this.totalEvents = 0;
      this.loadEventsWithFilters(1);
    }, 100);
  }

  // ── Data loading ───────────────────────────────────────────────────────────

  private resetAndLoad(): void {
    this.currentPage = 1;
    this.events = [];
    this.hasMore = true;
    this.totalEvents = 0;
    if (this.favouritesMode) {
      this.loadFavouriteEvents(1);
    } else if (this.featured) {
      this.loadFeaturedEvents();
    } else if (this.userName) {
      this.loadUserEvents(1);
    } else if (this.hasActiveFilters) {
      this.loadEventsWithFilters(1);
    } else {
      this.loadEvents(1);
    }
  }

  private loadFavouriteEvents(page: number = 1): void {
    if (this.isLoading) return;
    this.isLoading = true;
    this.cdr.detectChanges();
    this.favouriteService.getFavourites(page, USER_PAGE_SIZE, 'EVENT').subscribe({
      next: (res: any) => {
        const data = res.data;
        const cards = this.mapEvents(data.items ?? []);
        this.events = page === 1 ? cards : [...this.events, ...cards];
        this.totalEvents = data.total ?? 0;
        this.hasMore = this.events.length < this.totalEvents;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: () => { this.isLoading = false; this.cdr.detectChanges(); }
    });
  }

  private loadFeaturedEvents(): void {
    if (this.isLoading) return;
    this.isLoading = true;
    this.cdr.detectChanges();
    this.eventService.getFeaturedEvents().subscribe({
      next: (res: any) => {
        const apiEvents: any[] = res?.data?.items ?? res?.data ?? [];
        this.events = this.mapEvents(apiEvents);
        this.hasMore = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: () => { this.isLoading = false; this.cdr.detectChanges(); },
    });
  }

  loadEvents(page: number = 1): void {
    if (this.userName) {
      this.loadUserEvents(page);
      return;
    }
    if (this.isLoading || !this.hasMore) return;
    this.isLoading = true;
    this.cdr.detectChanges();

    this.eventService.getEvents(page).subscribe({
      next: (res: any) => {
        const apiEvents: any[] = res?.data?.items || [];
        const meta = res?.data?.meta;
        if (meta) {
          this.totalEvents = meta.total ?? 0;
          if (page === 1) {
            this.minPrice = Math.floor(meta.totalMinPrice ?? 0);
            this.maxPrice = Math.ceil(meta.totalMaxPrice ?? 0);
            this.priceFrom = this.minPrice;
            this.priceTo = this.maxPrice;
          }
        }
        this.events = [...this.events, ...this.mapEvents(apiEvents)];
        this.hasMore = this.events.length < this.totalEvents;
        this.isLoading = false;
        this.cdr.detectChanges();
        // spanAllCards fires automatically via eventCardRefs.changes subscription
      },
      error: () => { this.isLoading = false; this.cdr.detectChanges(); },
    });
  }

  loadEventsWithFilters(page: number = 1): void {
    if (this.userName) {
      this.loadUserEvents(page);
      return;
    }
    if (this.isLoading || !this.hasMore) return;
    this.isLoading = true;
    this.cdr.detectChanges();

    const filters: any = {};
    if (this.priceFrom !== this.minPrice) filters.priceMin = this.priceFrom;
    if (this.priceTo !== this.maxPrice) filters.priceMax = this.priceTo;
    if (this.locationText.trim()) filters.location = this.locationText.trim();
    if (this.searchText.trim()) {
      const terms = this.searchText.trim().split(/\s+/).map(t => t.replace(/^#+/, '')).filter(Boolean);
      filters.search = terms.join(',');
    }
    if (this.byDate) filters.startDate = this.byDate;
    if (this.selectedCategories.length) filters.categories = this.selectedCategories.join(',');

    this.eventService.getEvents(page, filters).subscribe({
      next: (res: any) => {
        const apiEvents: any[] = res?.data?.items || [];
        const meta = res?.data?.meta;
        if (meta) { this.totalEvents = meta.total ?? 0; }
        this.events = [...this.events, ...this.mapEvents(apiEvents)];
        this.hasMore = this.events.length < this.totalEvents;
        this.isLoading = false;
        this.cdr.detectChanges();
        // spanAllCards fires automatically via eventCardRefs.changes subscription
      },
      error: () => { this.isLoading = false; this.cdr.detectChanges(); },
    });
  }

  loadMore(): void {
    if (this.isLoading || !this.hasMore) return;
    this.currentPage++;
    if (this.favouritesMode) {
      this.loadFavouriteEvents(this.currentPage);
    } else if (this.userName) {
      this.loadUserEvents(this.currentPage);
    } else if (this.hasActiveFilters) {
      this.loadEventsWithFilters(this.currentPage);
    } else {
      this.loadEvents(this.currentPage);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.mixedMode) {
      if (changes['mixedItems']) {
        this.applyMixedItems();
      }
      return;
    }
    if (changes['userName'] && !changes['userName'].firstChange) {
      this.resetAndLoad();
    }
    if (changes['collabOnly'] && !changes['collabOnly'].firstChange) {
      this.resetAndLoad();
    }
  }

  /** Renders items handed down by the "All" page mixed feed — no HTTP call. */
  private applyMixedItems(): void {
    this.events = this.mapEvents(this.mixedItems ?? []);
    this.totalEvents = this.events.length;
    this.isLoading = false;
    this.hasMore = false;
    this.cdr.detectChanges();
    if (isPlatformBrowser(this.platformId)) {
      this.zone.runOutsideAngular(() => {
        requestAnimationFrame(() => requestAnimationFrame(() => this.spanAllCards()));
      });
    }
  }

  private get hasActiveFilters(): boolean {
    return !!(
      this.searchText || this.locationText || this.byDate ||
      this.priceFrom !== this.minPrice || this.priceTo !== this.maxPrice ||
      this.selectedCategories.length
    );
  }

  // ── trackBy for *ngFor ────────────────────────────────────────────────────

  trackById(_: number, event: EventCard): string { return event.id; }

  /** Single source of truth for the ui-avatars fallback used across this page. */
  avatarFallback(name?: string | null): string {
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'U')}&background=FFD4E0&color=D31148`;
  }

  // ── Map API response → EventCard ──────────────────────────────────────────

  private mapEvents(apiEvents: IEvent[]): EventCard[] {
    return apiEvents.map((ev: IEvent) => {
      const acceptedCollaborators = (ev.info?.collaborations ?? [])
        .filter(collab => collab.status === 'ACCEPTED')
        .map(collab => ({
          _id: collab.user?._id ?? '',
          displayName: collab.user?.displayName ?? '',
          userName: collab.user?.userName ?? '',
          profileImage: collab.user?.profileImage ?? undefined,
        }));
      const mentions = (ev.info?.mentions ?? []).map(u => ({
        _id: u._id ?? '',
        displayName: u.displayName ?? '',
        userName: u.userName ?? '',
        profileImage: u.profileImage ?? undefined,
      }));

      return {
        id: ev._id,
        hostName: ev?.user?.userName ?? 'Unknown',
        hostAvatar: ev?.user?.profileImage?.urlSmall ?? '',
        hostId: ev?.user?._id ?? '',
        hostGender: 'f' as const,
        timeAgo: this.getTimeAgo(ev?.createdAt),
        images: ev?.media?.length
          ? ev.media.map((m: any) => m.urlMedium || m.url)
          : [ev?.info?.logo].filter(Boolean),
        activeSlide: 0,
        title: ev?.title ?? '',
        startDateLabel: ev?.info?.startDate ? this.formatEventDateTime(ev.info.startDate) : '',
        endDateLabel: ev?.info?.endDate ? this.formatEventDateTime(ev.info.endDate) : '',
        startDateISO: ev?.info?.startDate?.split('T')[0] ?? '',
        location: ev?.info?.location ?? '',
        hashtags: ev?.hashtags ?? [],
        commentsDisabled: ev?.info?.commentsDisabled ?? false,
        linkToBookingSite: ev?.info?.linkToBookingSite ?? null,
        minPrice: Number(ev?.info?.priceMin || 0),
        maxPrice: Number(ev?.info?.priceMax || 0),
        likes: ev?.reactions?.total ?? ev?.numberOfLikes ?? 0,
        comments: ev?.numberOfComments ?? 0,
        shares: ev?.numberOfShares ?? 0,
        createdAt: ev?.createdAt ?? '',
        myReaction: ev?.reactions?.myReaction ?? null,
        reactionCounts: ev?.reactions?.counts ?? { LIKE: 0, HEART: 0, HUG: 0, WOW: 0, SAD: 0, HAHA: 0 },
        totalReactions: ev?.reactions?.total ?? 0,
        recentUserName: ev?.reactions?.recentUserName ?? null,
        topTypes: (ev?.reactions?.topTypes ?? []) as ReactionType[],
        showEmojiPicker: false,
        hoveredReaction: null,
        isMyPostedEvent: ev?.isMyPostedEvent ?? false,
        friendshipStatus: (ev?.friendship?.status ?? 'NONE') as FriendshipStatus,
        friendshipRequestId: ev?.friendship?.requestId ?? null,
        isFollowed: ev?.isFollowed ?? false,
        isFavourite: (ev as any)?.isFavourite ?? false,
        globalOrder: (ev as any)._order,
        acceptedCollaborators,
        mentions,
        isRemovedByAdmin: (ev as any)?.isRemovedByAdmin ?? false,
      };
    }).sort((a: EventCard, b: EventCard) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }

  private loadUserEvents(page: number = 1): void {
    if (this.isLoading || !this.hasMore) return;
    if (!this.userName) return;
    this.isLoading = true;
    this.cdr.detectChanges();

    this.eventService.getUserEvents(this.userName, page, USER_PAGE_SIZE, this.collabOnly).subscribe({
      next: (res: any) => {
        const apiEvents: any[] = res?.data?.items ?? [];
        const metaTotal = res?.data?.meta?.total ?? res?.data?.total ?? null;

        this.events = [...this.events, ...this.mapEvents(apiEvents)];
        if (metaTotal !== null) {
          this.totalEvents = metaTotal ?? 0;
          this.hasMore = this.events.length < this.totalEvents;
        } else {
          this.totalEvents = this.events.length;
          this.hasMore = apiEvents.length === USER_PAGE_SIZE;
        }

        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.isLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  // ── Getters ────────────────────────────────────────────────────────────────

  get filteredEvents(): EventCard[] { return this.events; }

  get fromPercent(): number {
    if (this.maxPrice === this.minPrice) return 0;
    return ((this.priceFrom - this.minPrice) / (this.maxPrice - this.minPrice)) * 100;
  }

  get toPercent(): number {
    if (this.maxPrice === this.minPrice) return 100;
    return ((this.priceTo - this.minPrice) / (this.maxPrice - this.minPrice)) * 100;
  }

  get priceLabel(): string { return `£${this.priceFrom} - £${this.priceTo}`; }

  get hasFilters(): boolean {
    return (
      !!this.searchText || !!this.locationText || !!this.byDate ||
      this.priceFrom !== this.minPrice || this.priceTo !== this.maxPrice ||
      this.selectedCategories.length > 0
    );
  }

  showCollabModal = false;
  selectedEvent: EventCard | null = null;
  showTaggedModal = false;

  showCollaboratorsModal(event: EventCard): void {
    this.selectedEvent = event;
    this.showCollabModal = true;
  }

  closeCollaboratorsModal(): void {
    this.showCollabModal = false;
    this.selectedEvent = null;
  }

  showTaggedPeopleModal(event: EventCard, e?: Event): void {
    e?.stopPropagation();
    this.selectedEvent = event;
    this.showTaggedModal = true;
  }

  closeTaggedPeopleModal(): void {
    this.showTaggedModal = false;
    this.selectedEvent = null;
  }

  clearAll(): void {
    this.searchText = '';
    this.searchInputValue = '';
    this.locationText = '';
    this.byDate = '';
    this.byDateDisplay = '';
    this.dateValue = null;
    // flatpickr keeps the picked date in its own input — resetting byDate above
    // leaves the box still showing it.
    this.datePicker?.clear();
    this.priceFrom = this.minPrice;
    this.priceTo = this.maxPrice;
    this.selectedCategories = [];
    this.resetAndLoad();
  }

  // ── Reaction modal ─────────────────────────────────────────────────────────

  openReactionModal(card: EventCard): void {
    this.isReactionOpen = true;
    this.activeReactionFilter = null;
    this.activeReactionEventId = card.id;
    this.loadReactions(card.id);
  }

  closeReactionModal(): void {
    this.isReactionOpen = false;
    this.reactionData = null;
    this.activeReactionEventId = null;
  }

  setReactionFilter(type: ReactionType | null): void {
    this.activeReactionFilter = type;
    if (this.activeReactionEventId) {
      this.loadReactions(this.activeReactionEventId, type ?? undefined);
    }
  }

  private loadReactions(eventId: string, type?: ReactionType): void {
    this.reactionsLoading = true;
    this.cdr.detectChanges();
    this.eventService.getReactions(eventId, type).subscribe({
      next: (res: any) => {
        this.reactionData = res?.data;
        this.reactionsLoading = false;
        this.cdr.detectChanges();
      },
      error: () => { this.reactionsLoading = false; this.cdr.detectChanges(); },
    });
  }

  get filteredReactions(): any[] { return this.reactionData?.all ?? []; }

  // ── Reaction card logic ────────────────────────────────────────────────────

  onReactionClick(card: EventCard): void {
    if (!this.isLoggedIn()) {
      this.openLoginPrompt(this.getEventDisplayName(card), 'react to this event by');
      return;
    }
    if (card.showEmojiPicker) {
      card.showEmojiPicker = false;
    } else {
      // this.soundService.playPickerOpen();
      this.events.forEach(e => e.showEmojiPicker = false);
      card.showEmojiPicker = true;
    }
    this.cdr.detectChanges();
  }

  onEmojiPick(card: EventCard, type: ReactionType, $event: MouseEvent): void {
    $event.stopPropagation();
    card.showEmojiPicker = false;
    card.hoveredReaction = null;
    // this.soundService.playReactionSelect(type);
    this.submitReaction(card, type);
  }

  closeEmojiPicker(card: EventCard): void {
    card.showEmojiPicker = false;
    card.hoveredReaction = null;
    this.cdr.detectChanges();
  }

  private submitReaction(card: EventCard, type: ReactionType): void {
    const previous = card.myReaction;
    const previousTopTypes = [...card.topTypes];
    const previousRecentUserName = card.recentUserName;

    if (previous === type) {
      card.reactionCounts[type] = Math.max(0, card.reactionCounts[type] - 1);
      card.totalReactions = Math.max(0, card.totalReactions - 1);
      card.myReaction = null;
    } else {
      if (previous) {
        card.reactionCounts[previous] = Math.max(0, card.reactionCounts[previous] - 1);
        card.totalReactions = Math.max(0, card.totalReactions - 1);
      }
      card.reactionCounts[type]++;
      card.totalReactions++;
      card.myReaction = type;
    }
    card.likes = card.totalReactions;

    // Update topTypes and recentUserName immediately
    card.topTypes = this.computeTopTypes(card.reactionCounts);
    const currentUserName = this.authState.getAuthSnapshot()?.user?.userName ?? null;
    if (card.myReaction) {
      card.recentUserName = currentUserName;
    } else if (card.totalReactions === 0) {
      card.recentUserName = null;
    }

    this.cdr.detectChanges();

    this.eventService.reactToEvent(card.id, type).subscribe({
      error: () => {
        card.myReaction = previous;
        card.reactionCounts = { ...card.reactionCounts };
        card.totalReactions = Object.values(card.reactionCounts).reduce((a, b) => a + b, 0);
        card.likes = card.totalReactions;
        card.topTypes = previousTopTypes;
        card.recentUserName = previousRecentUserName;
        this.cdr.detectChanges();
      },
    });
  }

  private computeTopTypes(counts: ReactionCounts): ReactionType[] {
    return (Object.entries(counts) as [ReactionType, number][])
      .filter(([, count]) => count > 0)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3)
      .map(([type]) => type);
  }

  getReactionConfig(type: ReactionType | null): typeof REACTIONS[0] | null {
    if (!type) return null;
    return REACTIONS.find(r => r.type === type) ?? null;
  }

  // ── Friend request ─────────────────────────────────────────────────────────

  onFriendButtonClick(card: EventCard, event: MouseEvent): void {
    event.stopPropagation();
    if (!this.isLoggedIn()) {
      this.openLoginPrompt(this.getEventDisplayName(card), 'add as a friend');
      return;
    }
    switch (card.friendshipStatus) {
      case 'NONE': this.sendFriendRequest(card, event); break;
      case 'PENDING_SENT': this.cancelFriendRequest(card, event); break;
      case 'PENDING_RECEIVED': this.acceptFriendRequest(card, event); break;
      case 'FRIENDS': this.promptUnfriend(card, event); break;
      case 'BLOCKED': break;
    }
  }

  showUnfriendConfirm = false;
  private pendingUnfriendAction: (() => void) | null = null;

  private promptUnfriend(card: EventCard, event: MouseEvent): void {
    event.stopPropagation();
    this.pendingUnfriendAction = () => this.unfriend(card, event);
    this.showUnfriendConfirm = true;
  }

  confirmUnfriend(): void {
    this.showUnfriendConfirm = false;
    this.pendingUnfriendAction?.();
    this.pendingUnfriendAction = null;
  }

  cancelUnfriendConfirm(): void {
    this.showUnfriendConfirm = false;
    this.pendingUnfriendAction = null;
  }

  friendButtonTitle(status: FriendshipStatus): string {
    const map: Record<FriendshipStatus, string> = {
      NONE: 'Send friend request',
      PENDING_SENT: 'Cancel friend request',
      PENDING_RECEIVED: 'Accept friend request',
      FRIENDS: 'Unfriend',
      BLOCKED: 'Blocked',
    };
    return map[status];
  }

  /** They've sent you a request — clicking the friend icon accepts it. (It
   *  used to call sendFriendRequest, which bails unless the status is NONE,
   *  so the button did nothing — SAF-839.) */
  private acceptFriendRequest(card: EventCard, event: MouseEvent): void {
    event.stopPropagation();
    if (card.friendshipStatus !== 'PENDING_RECEIVED' || !card.friendshipRequestId) return;
    card.friendshipStatus = 'FRIENDS';
    this.cdr.detectChanges();
    this.friendRequestService.accept(card.friendshipRequestId, card.hostId).subscribe({
      error: () => { card.friendshipStatus = 'PENDING_RECEIVED'; this.cdr.detectChanges(); },
    });
  }

  private sendFriendRequest(card: EventCard, event: MouseEvent): void {
    event.stopPropagation();
    if (card.friendshipStatus !== 'NONE') return;
    card.friendshipStatus = 'PENDING_SENT';
    this.cdr.detectChanges();
    this.friendRequestService.send(card.hostId).subscribe({
      next: (res: any) => { card.friendshipRequestId = res?.data?._id ?? null; this.cdr.detectChanges(); },
      error: (err) => {
        const status = err?.error?.statusCode ?? err?.status;
        if (status !== 409) { card.friendshipStatus = 'NONE'; }
        const cooldownHours = this.friendRequestService.parseCooldownError(err);
        if (cooldownHours !== null) {
          this.friendLimitHours = cooldownHours;
          this.showFriendLimitPopup = true;
        }
        this.cdr.detectChanges();
      },
    });
  }

  private cancelFriendRequest(card: EventCard, event: MouseEvent): void {
    event.stopPropagation();
    if (!card.friendshipRequestId) return;
    const prevStatus = card.friendshipStatus;
    const prevRequestId = card.friendshipRequestId;
    card.friendshipStatus = 'NONE';
    this.cdr.detectChanges();
    this.friendRequestService.cancel(card.friendshipRequestId).subscribe({
      error: () => { card.friendshipStatus = prevStatus; card.friendshipRequestId = prevRequestId; this.cdr.detectChanges(); },
    });
  }

  private unfriend(card: EventCard, event: MouseEvent): void {
    event.stopPropagation();
    const prevStatus = card.friendshipStatus;
    const prevRequestId = card.friendshipRequestId;
    card.friendshipStatus = 'NONE';
    card.friendshipRequestId = null;
    this.cdr.detectChanges();
    this.friendRequestService.unfriend(card.hostId).subscribe({
      error: () => { card.friendshipStatus = prevStatus; card.friendshipRequestId = prevRequestId; this.cdr.detectChanges(); },
    });
  }

  public follow(event: MouseEvent, card: EventCard): void {
    event.stopPropagation();
    if (!this.isLoggedIn()) {
      this.openLoginPrompt(this.getEventDisplayName(card), 'follow');
      return;
    }
    card.isFollowed = true;
    this.cdr.detectChanges();
    this.relationshipService.follow(card.hostId).subscribe({
      error: () => { card.isFollowed = false; this.cdr.detectChanges(); },
    });
  }

  public unfollow(event: MouseEvent, card: EventCard): void {
    event.stopPropagation();
    this.activeActionEventId = null;
    if (!this.isLoggedIn()) {
      this.openLoginPrompt(this.getEventDisplayName(card), 'unfollow');
      return;
    }
    card.isFollowed = false;
    this.cdr.detectChanges();
    this.relationshipService.unfollow(card.hostId).subscribe({
      error: () => { card.isFollowed = true; this.cdr.detectChanges(); },
    });
  }

  // ── Util ───────────────────────────────────────────────────────────────────

  getTimeAgo(dateString: string): string {
    const now = new Date().getTime();
    const past = new Date(dateString).getTime();
    const diff = Math.floor((now - past) / 1000);
    if (diff < 60) return 'just now';
    const m = Math.floor(diff / 60);
    if (m < 60) return `${m} minute${m > 1 ? 's' : ''} ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h} hour${h > 1 ? 's' : ''} ago`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d} day${d > 1 ? 's' : ''} ago`;
    const w = Math.floor(d / 7);
    if (d < 30) return `${w} week${w > 1 ? 's' : ''} ago`;
    const mo = Math.floor(d / 30);
    if (mo < 12) return `${mo} month${mo > 1 ? 's' : ''} ago`;
    const y = Math.floor(d / 365);
    return `${y} year${y > 1 ? 's' : ''} ago`;
  }

  formatEventDateTime(dateString: string): string {
    const d = new Date(dateString);
    const datePart = d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const timePart = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    return `${datePart}, ${timePart}`;
  }

  // ── Block user (from an event's 3-dot menu) ─────────────────────────────

  blockUser(card: EventCard, event: MouseEvent): void {
    event.stopPropagation();
    this.activeActionEventId = null;
    this.eventToBlock = card;
    this.showBlockUserConfirm = true;
    this.cdr.detectChanges();
  }

  cancelBlockEventUser(): void {
    this.showBlockUserConfirm = false;
    this.eventToBlock = null;
    this.cdr.detectChanges();
  }

  confirmBlockEventUser(): void {
    this.showBlockUserConfirm = false;
    const card = this.eventToBlock;
    this.eventToBlock = null;
    if (!card?.hostId) return;

    // Optimistically drop every event from this author out of the current
    // list — mirrors what a fresh feed fetch would return once blocked
    // users' content is filtered out server-side.
    const prevEvents = this.events;
    this.events = this.events.filter(e => e.hostId !== card.hostId);
    this.cdr.detectChanges();

    this.friendRequestService.block(card.hostId).subscribe({
      next: () => this.friendRequestService.triggerRefresh(),
      error: (err) => {
        console.error('Block user failed:', err);
        this.events = prevEvents;
        this.cdr.detectChanges();
      },
    });
  }

  goToEvent(eventId: string): void {
    this.router.navigate(['/event', eventId], {
      state: { returnUrl: this.router.url }
    });
  }

  goToEventComments(eventId: string, event: Event): void {
    event.stopPropagation();
    this.router.navigate(['/event', eventId], {
      queryParams: { openComments: '1' },
      state: { returnUrl: this.router.url }
    });
  }

  goToHashtag(tag: string, event?: Event): void {
    event?.stopPropagation();
    this.router.navigate(['/all'], { queryParams: { hashtag: tag } });
  }

  openBooking(link: string | null): void {
    if (link) { window.open(link, '_blank', 'noopener,noreferrer'); }
  }

  onModalFollowClick(item: any, event: MouseEvent): void {
    if (!this.isLoggedIn()) {
      this.openLoginPrompt(item.user?.userName ?? item.user?.userName, 'follow');
      return;
    }
    event.stopPropagation();
    if (item.isFollowed) {
      item.isFollowed = false;
      this.cdr.markForCheck();
      this.relationshipService.unfollow(item.user?._id).subscribe({
        error: () => { item.isFollowed = true; this.cdr.markForCheck(); }
      });
    } else {
      item.isFollowed = true;
      this.cdr.markForCheck();
      this.relationshipService.follow(item.user?._id).subscribe({
        error: () => { item.isFollowed = false; this.cdr.markForCheck(); }
      });
    }
  }

  onModalFriendClick(item: any, event: MouseEvent): void {
    if (!this.isLoggedIn()) {
      this.openLoginPrompt(item.user?.userName ?? item.user?.userName, 'add as a friend');
      return;
    }
    event.stopPropagation();
    const status = item.friendship.status ?? 'NONE';

    if (status === 'NONE') {
      item.friendship.status = 'PENDING_SENT';
      this.cdr.markForCheck();
      this.friendRequestService.send(item.user?._id).subscribe({
        next: (res: any) => { item.friendship.requestId = res?.data?._id ?? null; this.cdr.markForCheck(); },
        error: (err: any) => {
          const code = err?.error?.statusCode ?? err?.status;
          if (code !== 409) item.friendship.status = 'NONE';
          const cooldownHours = this.friendRequestService.parseCooldownError(err);
          if (cooldownHours !== null) {
            this.friendLimitHours = cooldownHours;
            this.showFriendLimitPopup = true;
          }
          this.cdr.markForCheck();
        }
      });
    } else if (status === 'PENDING_SENT') {
      const prev = item.friendship.status;
      const prevId = item.friendship.requestId;
      item.friendship.status = 'NONE';
      this.cdr.markForCheck();
      this.friendRequestService.cancel(item.friendship.requestId).subscribe({
        error: () => { item.friendship.status = prev; item.friendship.requestId = prevId; this.cdr.markForCheck(); }
      });
    } else if (status === 'FRIENDS') {
      this.pendingUnfriendAction = () => {
        const prev = item.friendship.status;
        item.friendship.status = 'NONE';
        this.cdr.markForCheck();
        this.friendRequestService.unfriend(item.user?._id).subscribe({
          error: () => { item.friendship.status = prev; this.cdr.markForCheck(); }
        });
      };
      this.showUnfriendConfirm = true;
    }
  }

  private isLoggedIn(): boolean {
    return this.authState.getAuthSnapshot()?.isLoggedIn === true;
  }

  private openLoginPrompt(userName: string, action: string): void {
    this.loginPromptUserName = userName;
    this.loginPromptAction = action;
    this.showLoginPrompt = true;
    this.cdr.detectChanges();
  }

  closeLoginPrompt(): void {
    this.showLoginPrompt = false;
    this.cdr.detectChanges();
  }

  goToLogin(): void {
    this.closeLoginPrompt();
    this.router.navigate(['/login']);
  }

  // ── Share ────────────────────────────────────────────────────────────────────
  activeShareItem: ShareConfig | null = null;
  activeShareCount = 0;
  private activeShareRef: any = null;

  openShareSheet(card: any, event: MouseEvent): void {
    event.stopPropagation();
    this.activeShareRef = card;
    this.activeShareCount = card.shares ?? 0;
    this.activeShareItem = {
      contentId: card.id,
      contentType: 'event',
      title: card.title ?? 'Check out this event',
      description: card.title,
      imageUrl: card.images?.[0],
    };
    this.cdr.detectChanges();
  }

  closeShareSheet(): void {
    this.activeShareItem = null;
    this.activeShareRef = null;
    this.cdr.detectChanges();
  }

  onShareCountUpdated(count: number): void {
    if (this.activeShareRef) this.activeShareRef.shares = count;
    this.cdr.detectChanges();
  }

  // ── Favourites ─────────────────────────────────────────────────────────────

  toggleFavourite(card: EventCard, event: MouseEvent): void {
    event.stopPropagation();
    if (!this.isLoggedIn()) {
      this.openLoginPrompt(card.hostName, 'save to favourites');
      return;
    }
    const prev = card.isFavourite;
    card.isFavourite = !prev;
    this.cdr.detectChanges();
    this.favouriteService.toggle(card.id).subscribe({
      next: (res: any) => {
        card.isFavourite = res?.data?.isFavourite ?? !prev;
        this.favouriteService.broadcastToggle(card.id, card.isFavourite);
        this.cdr.detectChanges();
      },
      error: () => { card.isFavourite = prev; this.cdr.detectChanges(); },
    });
  }

  private getEventDisplayName(card: EventCard): string {
    const collabNames = (card.acceptedCollaborators ?? [])
      .map(c => c.userName)
      .join(', ');
    return collabNames ? `${card.hostName} & ${collabNames}` : card.hostName;
  }

  // ── Event action menu (3-dot) ──────────────────────────────────────────────

  toggleEventActionMenu(event: EventCard, $event: MouseEvent): void {
    $event.stopPropagation();
    this.activeActionEventId = this.activeActionEventId === event.id ? null : event.id;
    this.cdr.detectChanges();
  }

  /** Event whose comments on/off request is in flight (disables its menu item). */
  togglingCommentsId: string | null = null;

  /** Turn comments on/off from the card's 3-dot menu — same API as event-details. */
  toggleComments(event: EventCard, $event: MouseEvent): void {
    $event.stopPropagation();
    if (this.togglingCommentsId) return;
    this.togglingCommentsId = event.id;
    this.eventService.toggleComments(event.id).subscribe({
      next: (res: any) => {
        event.commentsDisabled = res.data?.commentsDisabled ?? !event.commentsDisabled;
        this.togglingCommentsId = null;
        this.activeActionEventId = null;
        this.cdr.detectChanges();
      },
      error: () => {
        this.togglingCommentsId = null;
        this.cdr.detectChanges();
      },
    });
  }

  editEvent(event: EventCard, $event: MouseEvent): void {
    $event.stopPropagation();
    this.activeActionEventId = null;
    // Navigate to event detail with edit flag — edit modal opens on load
    this.router.navigate(['/event', event.id], { queryParams: { openEdit: '1' } });
    this.cdr.detectChanges();
  }

  confirmDeleteEvent(event: EventCard, $event: MouseEvent): void {
    $event.stopPropagation();
    this.activeActionEventId = null;
    this.eventToDelete = event;
    this.showDeleteEventConfirm = true;
    this.cdr.detectChanges();
  }

  cancelDeleteEvent(): void {
    this.showDeleteEventConfirm = false;
    this.eventToDelete = null;
    this.cdr.detectChanges();
  }

  doDeleteEvent(): void {
    if (!this.eventToDelete) return;
    const event = this.eventToDelete;
    this.showDeleteEventConfirm = false;
    this.eventToDelete = null;
    this.eventService.deleteEvent(event.id).subscribe({
      next: () => {
        this.events = this.events.filter(e => e.id !== event.id);
        this.eventService.triggerRefresh();
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error deleting event:', err);
        this.cdr.detectChanges();
      }
    });
  }

  reportEvent(event: EventCard, $event: MouseEvent): void {
    $event.stopPropagation();
    this.activeActionEventId = null;
    this.eventToReport = event;
    this.reportError = '';
    this.isReportModalOpen = true;
    this.cdr.detectChanges();
  }

  closeReportModal(): void {
    if (this.isSubmittingReport) return;
    this.isReportModalOpen = false;
    this.eventToReport = null;
    this.reportError = '';
    this.cdr.detectChanges();
  }

  submitEventReport(description: string): void {
    if (!this.eventToReport) return;
    this.isSubmittingReport = true;
    this.reportError = '';

    this.reportService.submitReport(this.eventToReport.id, description).subscribe({
      next: () => {
        this.zone.run(() => {
          this.isSubmittingReport = false;
          this.isReportModalOpen = false;
          this.eventToReport = null;
          this.reportSuccessOpen = true;
          this.cdr.detectChanges();
        });
      },
      error: (err) => {
        this.zone.run(() => {
          this.isSubmittingReport = false;
          this.reportError = err?.error?.message ?? 'Failed to submit report. Please try again.';
          this.cdr.detectChanges();
        });
      },
    });
  }

  closeReportSuccess(): void {
    this.reportSuccessOpen = false;
    this.cdr.detectChanges();
  }
}